import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardList, X, Check, Eye, EyeOff, MessageSquarePlus, Send, DollarSign, ThumbsUp, ThumbsDown, Mic } from "lucide-react";

const STATUS_LABEL = { new: "New", reviewed: "Reviewed", completed: "Completed" };
const STAGE_LABEL = { received: "Received", in_progress: "In progress", filed: "Filed", refund_sent: "Refund sent", info: "Info" };
const CATS = [
  ["drivers_license", "Driver's license / ID"],
  ["ssn_proof", "SSN cards"],
  ["proof_of_residency", "Residency"],
  ["form_1098t", "1098-T"],
  ["form_1095", "1095"],
  ["form_w2", "W-2"],
  ["form_1099", "1099"],
  ["child_care", "Child care"],
  ["self_employment_receipts", "SE receipts"],
  ["charitable_donations", "Donations"],
  ["mortgage_forms", "Mortgage"],
  ["retirement_forms", "Retirement"],
];

export default function AdminIntake() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const load = () => api.get("/admin/intake").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (id, reveal = false) => {
    try {
      const r = await api.get(`/admin/intake/${id}${reveal ? "?reveal=true" : ""}`);
      setDetail(r.data);
      setRevealed(reveal);
    } catch (e) { toast.error("Could not load"); }
  };
  const closeDetail = () => { setDetail(null); setRevealed(false); };
  const toggleReveal = () => open(detail.id, !revealed);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/intake/${id}?status=${status}`);
      toast.success("Updated");
      load();
      if (detail?.id === id) open(id, false); // re-mask after any write
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const postUpdate = async (id, payload) => {
    if (!payload.message?.trim() && payload.federal_refund == null && payload.net_to_client == null) {
      toast.error("Add a message or a refund breakdown first"); return;
    }
    try {
      const r = await api.post(`/admin/intake/${id}/status-update`, payload);
      if (r.data?.email_sent) toast.success("Update posted + refund confirmation emailed to client");
      else toast.success("Update posted — visible in client portal");
      open(id, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
  };

  const decideCashAdvance = async (id, decision, amount, notes) => {
    try {
      await api.post(`/admin/intake/${id}/cash-advance-decision`, {
        decision, amount_approved: amount || 0, notes: notes || "",
      });
      toast.success(`Cash advance ${decision} — client notified`);
      open(id, false); // re-mask
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save decision"); }
  };

  const toggleVerbal = async (id) => {
    try {
      const r = await api.post(`/admin/intake/${id}/verbal-approval`);
      if (r.data?.verbal_approved_at) toast.success("Verbal approval logged — you can submit to IRS");
      else toast.info("Verbal approval cleared");
      open(id, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3 flex items-center gap-2"><ClipboardList size={14} /> Intake forms</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">Client intakes.</h1>

        {rows.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No intakes yet. Share your intake link: <code className="text-xs">/intake</code></Card>
        ) : (
          <div className="space-y-2" data-testid="intake-list">
            {rows.map(r => (
              <Card key={r.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`intake-row-${r.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-serif text-lg">{r.first_name} {r.last_name}</div>
                  <div className="text-xs text-muted-foreground">{r.email} · {r.phone || "no phone"}</div>
                </div>
                <div className="text-xs text-muted-foreground hidden md:block">
                  {r.filing_status || "—"} · {r.dependents_count} dep · {r.uploads_count} files
                </div>
                <span className={`text-[10px] label-caps px-2 py-1 rounded ${r.status === "completed" ? "bg-primary/15 text-primary" : r.status === "reviewed" ? "bg-accent/15 text-accent" : "bg-secondary text-foreground"}`}>
                  {STATUS_LABEL[r.status] || r.status}
                </span>
                <div className="text-[10px] text-muted-foreground shrink-0">{new Date(r.submitted_at).toLocaleDateString()}</div>
                <Button size="sm" variant="outline" onClick={() => open(r.id)} data-testid={`intake-open-${r.id}`}><Eye size={12} className="mr-1" /> View</Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={closeDetail}>
          <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="intake-detail">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="label-caps text-[10px]">Intake · {detail.status}</div>
                  {detail.verbal_approved_at && (
                    <span className="text-[9px] label-caps px-1.5 py-0.5 rounded bg-primary/15 text-primary flex items-center gap-1" data-testid="verbal-approved-badge">
                      <Mic size={9} /> Verbal ✓
                    </span>
                  )}
                </div>
                <div className="font-serif text-2xl">{detail.first_name} {detail.last_name}</div>
                <div className="text-xs text-muted-foreground">{[detail.email, detail.phone].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={detail.verbal_approved_at ? "default" : "outline"}
                  onClick={() => toggleVerbal(detail.id)}
                  data-testid="verbal-approval-btn"
                  title={detail.verbal_approved_at ? "Clear verbal approval" : "Log client verbal approval"}
                >
                  <Mic size={12} className="mr-1" />
                  {detail.verbal_approved_at ? "Verbal ✓ logged" : "Client verbally approved"}
                </Button>
                <Button size="sm" variant={revealed ? "default" : "outline"} onClick={toggleReveal} data-testid="reveal-toggle-btn">
                  {revealed ? <><EyeOff size={12} className="mr-1" /> Hide SSN</> : <><Eye size={12} className="mr-1" /> Reveal SSN</>}
                </Button>
                {detail.status !== "reviewed" && detail.status !== "completed" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(detail.id, "reviewed")} data-testid="mark-reviewed-btn">Mark reviewed</Button>
                )}
                {detail.status !== "completed" && (
                  <Button size="sm" onClick={() => setStatus(detail.id, "completed")} data-testid="mark-completed-btn"><Check size={12} className="mr-1" /> Complete</Button>
                )}
                <button onClick={closeDetail} className="p-2 rounded-full hover:bg-secondary" aria-label="Close" data-testid="intake-detail-close"><X size={16} /></button>
              </div>
            </div>

            {detail.verbal_approved_at && (
              <div className="mx-4 mt-4 p-3 rounded-md border border-primary/40 bg-primary/5 text-xs" data-testid="verbal-approval-note">
                <strong>Verbal approval logged</strong> · {new Date(detail.verbal_approved_at).toLocaleString()}
                {detail.verbal_approved_by_email && <> · by {detail.verbal_approved_by_email}</>}
              </div>
            )}

            <div className="p-6 space-y-6 text-sm">
              <Grid>
                <KV k="DOB" v={detail.dob} />
                <KV k="SSN" v={detail.ssn} />
                <KV k="Filing status" v={detail.filing_status} />
              </Grid>
              <KV k="Address" v={[detail.address, detail.city, [detail.state, detail.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")} />

              {detail.filing_status === "mfj" && (detail.spouse_first_name || detail.spouse_last_name || detail.spouse_dob || detail.spouse_ssn) && (
                <div className="border-l-2 border-border pl-4">
                  <div className="label-caps mb-1">Spouse</div>
                  <div>
                    {[
                      [detail.spouse_first_name, detail.spouse_last_name].filter(Boolean).join(" ") || null,
                      detail.spouse_dob ? `DOB ${detail.spouse_dob}` : null,
                      detail.spouse_ssn ? `SSN ${detail.spouse_ssn}` : null,
                    ].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              )}

              {detail.dependents?.length > 0 && (
                <div>
                  <div className="label-caps mb-2">Dependents ({detail.dependents.length})</div>
                  <div className="space-y-1">
                    {detail.dependents.map((d, i) => {
                      const name = [d.first_name, d.last_name].filter(Boolean).join(" ");
                      const parts = [
                        d.relation || null,
                        d.dob ? `DOB ${d.dob}` : null,
                        d.ssn ? `SSN ${d.ssn}` : null,
                      ].filter(Boolean);
                      return (
                        <div key={i} className="text-xs border-l-2 border-border pl-3">
                          <strong>{name || `Dependent #${i + 1}`}</strong>
                          {parts.length > 0 && <> · {parts.join(" · ")}</>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(detail.w2_employers || detail.ten99_payers || detail.self_employment || detail.rental_income || detail.investment_income) && (
                <div>
                  <div className="label-caps mb-2">Income</div>
                  <div className="space-y-2 text-xs">
                    {detail.w2_employers && <div><strong>W-2:</strong> {detail.w2_employers}</div>}
                    {detail.ten99_payers && <div><strong>1099:</strong> {detail.ten99_payers}</div>}
                    {detail.self_employment && <div><strong>Self-employment:</strong> {detail.self_employment}</div>}
                    {detail.rental_income && <div><strong>Rental:</strong> {detail.rental_income}</div>}
                    {detail.investment_income && <div><strong>Investment:</strong> {detail.investment_income}</div>}
                  </div>
                </div>
              )}

              <div>
                <div className="label-caps mb-2">Deductions / credits</div>
                <Grid>
                  <KV k="Itemize?" v={detail.itemize ? "Yes" : "Standard"} />
                  {detail.itemize && <>
                    <KV k="Mortgage" v={detail.itemized_mortgage} />
                    <KV k="Property tax" v={detail.itemized_property_tax} />
                    <KV k="Medical" v={detail.itemized_medical} />
                    <KV k="Charity" v={detail.itemized_charity} />
                  </>}
                  <KV k="Child care" v={detail.credit_childcare} />
                  <KV k="Education" v={detail.credit_education} />
                  <KV k="EV" v={detail.credit_ev} />
                  <KV k="Energy" v={detail.credit_energy} />
                </Grid>
              </div>

              <div>
                <div className="label-caps mb-2">Documents</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {CATS.map(([k, label]) => {
                    const files = detail.uploads?.[k] || [];
                    return (
                      <div key={k} className="p-2 border border-border rounded text-xs">
                        <div className="font-medium mb-1">{label} ({files.length})</div>
                        {files.length === 0 ? <div className="text-muted-foreground text-[10px]">—</div> : (
                          <div className="space-y-1">
                            {files.map((p, idx) => (
                              <a key={idx} href={`${BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer" className="block underline truncate text-primary" data-testid={`doc-link-${k}-${idx}`}>
                                Open file {idx + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="label-caps mb-2">Refund delivery</div>
                <Grid>
                  <KV k="Method" v={
                    detail.refund_method === "ra_card" ? "Refund Advantage Card" :
                    detail.refund_method === "direct_deposit" ? "Direct Deposit" :
                    detail.refund_method === "ra_check" ? "Check from Refund Advantage" :
                    "—"
                  } />
                  <KV k="Bank name" v={detail.bank_name} />
                  <KV k="Routing" v={detail.bank_routing} />
                  <KV k="Account" v={detail.bank_account} />
                  <KV k="Type" v={detail.bank_account_type} />
                </Grid>
              </div>

              {detail.disclaimer_cash_advance && (
                <CashAdvancePanel intake={detail} onDecide={decideCashAdvance} />
              )}

              {detail.notes && (
                <div>
                  <div className="label-caps mb-1">Notes</div>
                  <div className="text-xs whitespace-pre-wrap italic">{detail.notes}</div>
                </div>
              )}

              <StatusUpdatesPanel intake={detail} onPost={postUpdate} />

              <div className="border-t border-border pt-4">
                <div className="label-caps mb-2">Signature</div>
                <div className="text-xs">Signed by <strong>{detail.signature}</strong> on {detail.signed_date}</div>
                <div className="text-[10px] mt-2 text-muted-foreground">
                  Accuracy disclaimer: {detail.disclaimer_accuracy ? "✓ accepted" : "✗ not accepted"} · Cash advance: {detail.disclaimer_cash_advance ? "✓ accepted" : "not applying"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const Grid = ({ children }) => <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">{children}</div>;
const KV = ({ k, v }) => (
  <div>
    <div className="label-caps text-[9px]">{k}</div>
    <div className="text-sm">{v || "—"}</div>
  </div>
);

const STAGES = [
  { key: "received", label: "Received" },
  { key: "in_progress", label: "In progress" },
  { key: "filed", label: "Filed" },
  { key: "refund_sent", label: "Refund sent" },
  { key: "info", label: "Info" },
];

function CashAdvancePanel({ intake, onDecide }) {
  const decision = intake.cash_advance_decision || "pending";
  const [amount, setAmount] = React.useState(intake.cash_advance_amount_approved || "");
  const [notes, setNotes] = React.useState(intake.cash_advance_decision_notes || "");
  const decidedAt = intake.cash_advance_decided_at;
  const badge = decision === "approved"
    ? "bg-primary/15 text-primary"
    : decision === "denied"
      ? "bg-destructive/15 text-destructive"
      : "bg-secondary text-foreground";

  return (
    <div className="border-t border-border pt-4" data-testid="cash-advance-panel">
      <div className="flex items-center gap-2 mb-3 text-primary">
        <DollarSign size={12} />
        <span className="label-caps text-[10px]">Cash advance · Refund Advantage / Pathway</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-xs mb-4">
        <div>
          <div className="label-caps text-[9px]">Client's expected refund</div>
          <div className="text-sm">{intake.cash_advance_expected_refund
            ? `$${Number(String(intake.cash_advance_expected_refund).replace(/[^0-9.-]/g, "") || 0).toLocaleString()}`
            : "—"}
          </div>
        </div>
        <div>
          <div className="label-caps text-[9px]">Current decision</div>
          <div><span className={`label-caps text-[9px] px-2 py-1 rounded ${badge}`} data-testid="cash-advance-status">{decision}</span></div>
          {decidedAt && <div className="text-[10px] text-muted-foreground mt-1">Decided {new Date(decidedAt).toLocaleString()}</div>}
        </div>
        {decision === "approved" && (
          <div>
            <div className="label-caps text-[9px]">Amount approved</div>
            <div className="text-sm font-medium text-primary">${Number(intake.cash_advance_amount_approved || 0).toLocaleString()}</div>
          </div>
        )}
        {intake.cash_advance_decision_notes && (
          <div className="sm:col-span-2">
            <div className="label-caps text-[9px]">Notes on file</div>
            <div className="text-xs italic">{intake.cash_advance_decision_notes}</div>
          </div>
        )}
      </div>

      {decision === "pending" && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="label-caps text-[9px]">Amount approved (if approving)</div>
              <input
                type="number" min="0" step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="3000"
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-xs"
                data-testid="cash-advance-amount"
              />
            </div>
            <div className="space-y-1">
              <div className="label-caps text-[9px]">Notes (optional)</div>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Approved pending IRS acceptance"
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-xs"
                data-testid="cash-advance-notes"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onDecide(intake.id, "approved", Number(amount), notes)} data-testid="cash-advance-approve">
              <ThumbsUp size={12} className="mr-1" /> Approve
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onDecide(intake.id, "denied", 0, notes)} data-testid="cash-advance-deny">
              <ThumbsDown size={12} className="mr-1" /> Deny
            </Button>
          </div>
        </div>
      )}

      {decision !== "pending" && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground" data-testid="cash-advance-override-toggle">Override this decision…</summary>
          <div className="mt-3 space-y-3 border-l-2 border-border pl-3">
            <div className="text-[10px] text-muted-foreground">
              Only override if the previous decision was recorded incorrectly. This will post a new client-visible update.
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="number" min="0" step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="New amount (if approving)"
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-xs"
                data-testid="cash-advance-override-amount"
              />
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for override"
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-xs"
                data-testid="cash-advance-override-notes"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onDecide(intake.id, "approved", Number(amount), notes)} data-testid="cash-advance-override-approve">
                Re-approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDecide(intake.id, "denied", 0, notes)} data-testid="cash-advance-override-deny">
                Re-deny
              </Button>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

function StatusUpdatesPanel({ intake, onPost }) {
  const [stage, setStage] = React.useState("info");
  const [message, setMessage] = React.useState("");
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  const [breakdown, setBreakdown] = React.useState({
    federal_refund: "", state_refund: "", preparation_fee: "", bank_fee: "", net_to_client: "",
    disbursement_method: "", estimated_funding_date: "",
  });
  const [sendEmail, setSendEmail] = React.useState(true);
  const updates = (intake.status_updates || []).slice().reverse();

  const num = (v) => v === "" || v == null ? null : Number(v);

  const submit = async () => {
    const payload = { stage, message };
    if (showBreakdown) {
      Object.assign(payload, {
        federal_refund: num(breakdown.federal_refund),
        state_refund: num(breakdown.state_refund),
        preparation_fee: num(breakdown.preparation_fee),
        bank_fee: num(breakdown.bank_fee),
        net_to_client: num(breakdown.net_to_client),
        disbursement_method: breakdown.disbursement_method,
        estimated_funding_date: breakdown.estimated_funding_date,
        send_email: sendEmail,
      });
    }
    await onPost(intake.id, payload);
    setMessage("");
    if (showBreakdown) {
      setBreakdown({ federal_refund: "", state_refund: "", preparation_fee: "", bank_fee: "", net_to_client: "", disbursement_method: "", estimated_funding_date: "" });
    }
  };

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center gap-2 mb-3 text-primary">
        <MessageSquarePlus size={12} />
        <span className="label-caps text-[10px]">Refund updates for client portal</span>
      </div>
      {updates.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="admin-updates-list">
          {updates.map((u) => (
            <div key={u.id} className="border-l-2 border-primary pl-3 text-xs">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="label-caps text-[9px]">{STAGE_LABEL[u.stage] || u.stage}</span>
                <span className="text-[9px] text-muted-foreground">{new Date(u.posted_at).toLocaleString()}</span>
                {u.breakdown && <span className="text-[9px] label-caps px-1.5 py-0.5 rounded bg-accent/20 text-accent">Refund conf.</span>}
              </div>
              <div className="whitespace-pre-wrap">{u.message}</div>
              {u.breakdown && (
                <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-muted-foreground">
                  {u.breakdown.federal_refund != null && <div>Federal: ${Number(u.breakdown.federal_refund).toFixed(2)}</div>}
                  {u.breakdown.state_refund != null && <div>State: ${Number(u.breakdown.state_refund).toFixed(2)}</div>}
                  {u.breakdown.preparation_fee != null && <div>Prep fee: −${Number(u.breakdown.preparation_fee).toFixed(2)}</div>}
                  {u.breakdown.bank_fee != null && <div>Bank fee: −${Number(u.breakdown.bank_fee).toFixed(2)}</div>}
                  {u.breakdown.net_to_client != null && <div className="font-semibold text-foreground">Net: ${Number(u.breakdown.net_to_client).toFixed(2)}</div>}
                  {u.breakdown.disbursement_method && <div>via {u.breakdown.disbursement_method}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 items-end">
        <div className="col-span-1">
          <div className="label-caps text-[9px] mb-1">Stage</div>
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
            data-testid="admin-update-stage"
          >
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-span-3">
          <div className="label-caps text-[9px] mb-1">Message to client</div>
          <textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Return filed today — IRS accepted. Refund typically lands within 21 days."
            className="w-full rounded-md border border-input bg-background p-2 text-xs"
            data-testid="admin-update-message"
          />
        </div>
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowBreakdown((s) => !s)}
          className="text-[11px] underline text-muted-foreground hover:text-foreground"
          data-testid="admin-update-toggle-breakdown"
        >
          {showBreakdown ? "− Hide refund breakdown" : "+ Attach refund confirmation (email screenshot to client)"}
        </button>
      </div>

      {showBreakdown && (
        <div className="mt-3 p-3 rounded-md border border-accent/40 bg-accent/5" data-testid="admin-update-breakdown">
          <div className="label-caps text-[10px] mb-2 text-accent">Refund breakdown · emailed as receipt</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-2">
            <NumField label="Federal ($)" value={breakdown.federal_refund} onChange={(v) => setBreakdown({ ...breakdown, federal_refund: v })} testid="admin-bd-federal" />
            <NumField label="State ($)" value={breakdown.state_refund} onChange={(v) => setBreakdown({ ...breakdown, state_refund: v })} testid="admin-bd-state" />
            <NumField label="Prep fee ($)" value={breakdown.preparation_fee} onChange={(v) => setBreakdown({ ...breakdown, preparation_fee: v })} testid="admin-bd-prep" />
            <NumField label="Bank fee ($)" value={breakdown.bank_fee} onChange={(v) => setBreakdown({ ...breakdown, bank_fee: v })} testid="admin-bd-bank" />
            <NumField label="Net to client ($)" value={breakdown.net_to_client} onChange={(v) => setBreakdown({ ...breakdown, net_to_client: v })} testid="admin-bd-net" />
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mb-2">
            <div>
              <div className="label-caps text-[9px] mb-1">Disbursement</div>
              <select
                value={breakdown.disbursement_method}
                onChange={(e) => setBreakdown({ ...breakdown, disbursement_method: e.target.value })}
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                data-testid="admin-bd-method"
              >
                <option value="">Choose…</option>
                <option>Refund Advantage Card</option>
                <option>Direct Deposit</option>
                <option>Check from Refund Advantage</option>
              </select>
            </div>
            <div>
              <div className="label-caps text-[9px] mb-1">Est. funding date</div>
              <input
                value={breakdown.estimated_funding_date}
                onChange={(e) => setBreakdown({ ...breakdown, estimated_funding_date: e.target.value })}
                placeholder="e.g., By Feb 26, 2026"
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                data-testid="admin-bd-funding"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="admin-bd-send-email" />
            <span>Email the client a screenshot-style receipt (recommended)</span>
          </label>
        </div>
      )}

      <Button
        size="sm"
        className="mt-3"
        onClick={submit}
        data-testid="admin-update-post"
      >
        <Send size={12} className="mr-1" /> {showBreakdown && sendEmail ? "Post & email receipt" : "Post update"}
      </Button>
    </div>
  );
}

const NumField = ({ label, value, onChange, testid }) => (
  <div>
    <div className="label-caps text-[9px] mb-1">{label}</div>
    <input
      type="number"
      inputMode="decimal"
      step="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
      data-testid={testid}
    />
  </div>
);
